import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useOfferStore } from '@/lib/store';
import { Download, FileText, AlertCircle, HelpCircle, CheckCircle, ShieldCheck, Loader2, FileDown, ChevronDown, Settings2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface GAEBExportOptions {
  exportFormat: 'x83' | 'x84' | 'x85';
}

interface GAEBExportProps {
  defaultFormat?: 'x84' | 'x85';
}

interface ValidationResult {
  isValid: boolean;
  schemaValid: boolean;
  schemaErrors: string[];
  summary: string;
}

export default function GAEBExport({ defaultFormat = 'x84' }: GAEBExportProps) {
  const isGAEBDatabase = useOfferStore(s => s.isGAEBDatabase);
  const gaebMetadata = useOfferStore(s => s.gaebMetadata);
  const positions = useOfferStore(s => s.positions);
  const currentDatabaseId = useOfferStore(s => s.currentDatabaseId);
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [options, setOptions] = useState<GAEBExportOptions>({
    exportFormat: defaultFormat
  });

  // Sync with defaultFormat prop when it changes
  useEffect(() => {
    setOptions(prev => ({ ...prev, exportFormat: defaultFormat }));
  }, [defaultFormat]);

  // Calculate statistics
  const stats = useMemo(() => {
    const items = positions.filter(p => (p as any).type === 'item');
    const withPrices = items.filter(p => {
      const gaeb = p as any;
      return gaeb.ourPrice !== undefined && gaeb.ourPrice > 0;
    });
    const withComments = items.filter(p => {
      const gaeb = p as any;
      return gaeb.ourComment && gaeb.ourComment.trim();
    });
    const withTextComplement = items.filter(p => {
      const gaeb = p as any;
      return gaeb.gaebTextComplementMarkers?.length > 0;
    });
    const textComplementFilled = withTextComplement.filter(p => {
      const gaeb = p as any;
      return gaeb.ourComment && gaeb.ourComment.trim();
    });
    const withChangedQty = items.filter(p => {
      const gaeb = p as any;
      const orig = gaeb.originalQuantity ?? gaeb.menge ?? 1;
      return gaeb.adjustedQuantity !== undefined && gaeb.adjustedQuantity !== orig;
    });

    return {
      total: items.length,
      withPrices: withPrices.length,
      withComments: withComments.length,
      withTextComplement: withTextComplement.length,
      textComplementFilled: textComplementFilled.length,
      withChangedQty: withChangedQty.length,
      completionRate: items.length > 0 ? Math.round((withPrices.length / items.length) * 100) : 0
    };
  }, [positions]);

  // Only show if we're in GAEB mode
  if (!isGAEBDatabase || !gaebMetadata || !currentDatabaseId) {
    return null;
  }

  // Save current prices/comments to database before exporting
  const saveChanges = async () => {
    try {
      const entriesToSave = positions
        .filter(p => {
          const gaebEntry = p as any;
          return gaebEntry.type === 'item' && (
            gaebEntry.ourPrice !== undefined ||
            (gaebEntry.ourComment && gaebEntry.ourComment.trim()) ||
            gaebEntry.adjustedQuantity !== undefined
          );
        })
        .map(p => {
          const gaebEntry = p as any;
          return {
            artikelId: gaebEntry.artikelId || gaebEntry.originalId || String(p.id),
            ourPrice: gaebEntry.ourPrice === undefined ? null : gaebEntry.ourPrice,
            ourComment: (!gaebEntry.ourComment || gaebEntry.ourComment === '') ? null : gaebEntry.ourComment,
            adjustedQuantity: gaebEntry.adjustedQuantity === undefined ? null : gaebEntry.adjustedQuantity
          };
        });

      if (entriesToSave.length === 0) return true;

      const response = await fetch('/api/gaeb/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ databaseId: currentDatabaseId, entries: entriesToSave })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Save failed' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Speichern fehlgeschlagen",
        description: error instanceof Error ? error.message : 'Fehler beim Speichern',
        variant: "destructive",
      });
      return false;
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    setValidationResult(null);
    try {
      await saveChanges();
      const response = await fetch('/api/gaeb/validate-xml', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ databaseId: currentDatabaseId, exportFormat: options.exportFormat })
      });
      const result = await response.json();
      setValidationResult({
        isValid: result.isValid,
        schemaValid: result.schemaValid,
        schemaErrors: result.schemaErrors || [],
        summary: result.summary
      });
      toast({
        title: result.isValid ? "Validierung erfolgreich" : "Validierungsfehler",
        description: result.isValid ? "XML ist schema-konform" : `${result.schemaErrors?.length || 0} Fehler`,
        variant: result.isValid ? "default" : "destructive",
      });
    } catch (error) {
      toast({
        title: "Validierung fehlgeschlagen",
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: "destructive",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleExport = async () => {
    if (!currentDatabaseId) return;
    setIsExporting(true);
    try {
      const saved = await saveChanges();
      if (!saved) return;

      const response = await fetch('/api/gaeb/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          databaseId: currentDatabaseId,
          includeComments: true,
          exportFormat: options.exportFormat
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Export fehlgeschlagen' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const baseName = gaebMetadata.originalFilename.replace(/\.(xml|d83|x83|d84|x84|d85|x85|p83|p84|p85)$/i, '');
      const ext = options.exportFormat.toUpperCase();
      link.download = `${baseName}_${timestamp}.${ext}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: `${ext} exportiert`,
        description: link.download,
      });
      setIsOpen(false);
    } catch (error) {
      toast({
        title: "Export fehlgeschlagen",
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!currentDatabaseId) return;
    setIsExportingPDF(true);
    try {
      const saved = await saveChanges();
      if (!saved) return;

      const response = await fetch('/api/gaeb/export-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ databaseId: currentDatabaseId, includeComments: true })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'PDF Export fehlgeschlagen' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const baseName = gaebMetadata.originalFilename.replace(/\.(xml|d83|x83|d84|x84|p83|p84)$/i, '');
      link.download = `${baseName}_LV_${timestamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({ title: "PDF exportiert", description: link.download });
    } catch (error) {
      toast({
        title: "PDF Export fehlgeschlagen",
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: "destructive",
      });
    } finally {
      setIsExportingPDF(false);
    }
  };

  const isLoading = isExporting || isValidating || isExportingPDF;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center space-x-2 border-orange-200 text-orange-700 hover:bg-orange-50">
          <Download className="h-4 w-4" />
          <span>Export</span>
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-orange-600" />
            <span>GAEB Export</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {gaebMetadata.projectName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Completion Status - Compact */}
          <div className={`flex items-center justify-between p-2 rounded-lg ${stats.completionRate === 100 ? 'bg-green-50' : 'bg-gray-50'}`}>
            <div className="text-sm">
              <span className="font-semibold">{stats.withPrices}</span>
              <span className="text-gray-500">/{stats.total} bepreist</span>
            </div>
            <Badge variant={stats.completionRate === 100 ? "default" : "secondary"}
                   className={stats.completionRate === 100 ? 'bg-green-600' : ''}>
              {stats.completionRate}%
            </Badge>
          </div>

          {/* TextComplement Status */}
          {stats.withTextComplement > 0 && (
            <div className="flex items-center justify-between text-xs text-gray-600 px-1">
              <span>Textergänzungen:</span>
              <span className={stats.textComplementFilled === stats.withTextComplement ? 'text-green-600 font-medium' : 'text-blue-600'}>
                {stats.textComplementFilled}/{stats.withTextComplement} ausgefüllt
              </span>
            </div>
          )}

          {/* Main Export Button - X84 */}
          <Button
            onClick={handleExport}
            disabled={isLoading}
            className="w-full bg-orange-600 hover:bg-orange-700 h-12"
          >
            {isExporting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Exportiere...</>
            ) : (
              <><Download className="h-4 w-4 mr-2" />X84 Angebot exportieren</>
            )}
          </Button>

          {/* Quantity Change Warning for X84 */}
          {stats.withChangedQty > 0 && options.exportFormat === 'x84' && (
            <div className="flex items-start space-x-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-amber-800">
                <span className="font-medium">{stats.withChangedQty} Mengenänderung(en)</span> werden in X84 nicht übertragen.
                <button
                  onClick={() => { setShowAdvanced(true); setOptions(prev => ({ ...prev, exportFormat: 'x85' })); }}
                  className="block mt-1 text-amber-700 underline hover:text-amber-900"
                >
                  → X85 für Mengenänderungen verwenden
                </button>
              </div>
            </div>
          )}

          {/* PDF Export */}
          <Button
            variant="outline"
            onClick={handleExportPDF}
            disabled={isLoading}
            className="w-full text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
          >
            {isExportingPDF ? (
              <><Loader2 className="h-3 w-3 mr-1 animate-spin" />PDF wird erstellt...</>
            ) : (
              <><FileDown className="h-3 w-3 mr-1" />PDF mit Kommentaren</>
            )}
          </Button>

          {/* Advanced Options - Collapsible */}
          <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
            <CollapsibleTrigger asChild>
              <button className="flex items-center justify-between w-full text-xs text-gray-500 hover:text-gray-700 py-1">
                <span className="flex items-center gap-1">
                  <Settings2 className="h-3 w-3" />
                  Erweiterte Optionen
                </span>
                <ChevronDown className={`h-3 w-3 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-2">
              {/* Format Selection */}
              <Card className="border-gray-200">
                <CardContent className="pt-3 pb-3 space-y-2">
                  <div className="flex items-center gap-1 text-xs font-medium text-gray-700">
                    <span>Exportformat</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3 w-3 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs text-xs">
                          <p><strong>X84</strong> = Standard-Preisabgabe</p>
                          <p><strong>X85</strong> = Nebenangebot (Mengenänderungen)</p>
                          <p><strong>X83</strong> = Vollständig (alle Texte)</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  <div className="grid grid-cols-3 gap-1">
                    {(['x84', 'x85', 'x83'] as const).map((fmt) => (
                      <button
                        key={fmt}
                        onClick={() => setOptions(prev => ({ ...prev, exportFormat: fmt }))}
                        className={`px-2 py-1.5 text-xs rounded border transition-colors ${
                          options.exportFormat === fmt
                            ? 'bg-orange-100 border-orange-300 text-orange-800 font-medium'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {fmt.toUpperCase()}
                        {fmt === 'x84' && <span className="block text-[9px] text-gray-500">Standard</span>}
                        {fmt === 'x85' && <span className="block text-[9px] text-gray-500">Alternativ</span>}
                        {fmt === 'x83' && <span className="block text-[9px] text-gray-500">Vollständig</span>}
                      </button>
                    ))}
                  </div>

                  {options.exportFormat !== 'x84' && (
                    <Button
                      onClick={handleExport}
                      disabled={isLoading}
                      variant="outline"
                      className="w-full mt-2 text-xs"
                    >
                      {options.exportFormat.toUpperCase()} exportieren
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Validate */}
              <Button
                variant="outline"
                onClick={handleValidate}
                disabled={isLoading}
                size="sm"
                className="w-full text-xs"
              >
                {isValidating ? (
                  <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Prüfe...</>
                ) : (
                  <><ShieldCheck className="h-3 w-3 mr-1" />Schema validieren</>
                )}
              </Button>

              {/* Validation Result */}
              {validationResult && (
                <div className={`p-2 rounded text-xs ${
                  validationResult.isValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center space-x-2">
                    {validationResult.isValid ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    )}
                    <span className={validationResult.isValid ? 'text-green-800' : 'text-red-800'}>
                      {validationResult.isValid ? 'Schema gültig' : `${validationResult.schemaErrors.length} Fehler`}
                    </span>
                  </div>
                  {!validationResult.isValid && validationResult.schemaErrors.length > 0 && (
                    <ul className="mt-1 text-red-700 text-[10px] list-disc list-inside">
                      {validationResult.schemaErrors.slice(0, 2).map((err, i) => (
                        <li key={i} className="truncate">{err}</li>
                      ))}
                      {validationResult.schemaErrors.length > 2 && (
                        <li>... +{validationResult.schemaErrors.length - 2} weitere</li>
                      )}
                    </ul>
                  )}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>
      </DialogContent>
    </Dialog>
  );
}
